import * as React from 'react';
import { Loader, Table } from 'semantic-ui-react';

import { Resultados } from 'src/api';
import { EstadoCurso } from '../../estadoCurso';
import Curso, { Props as CursoProps } from './Curso';

type CursosDTO = Resultados<'cursosQueElAlumnoPuedeInscribir'>['cursos'];
type BloqueHorarioDTO = CursosDTO[number]['bloqueHorario'];
const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

interface Dia {
    cursos: CursoProps[];
}
interface BloqueHorario {
    nombre: string;
    dias: Dia[];
}

interface Props {
    cursos: CursosDTO;
    cargando?: boolean;
    alClickearCurso?(props: CursoProps): void;
}

const TablaDeCursos: React.FunctionComponent<Props> = props => {
    const { cursos, alClickearCurso, cargando = false } = props;

    const bloquesHorarios = transformarRespuestaACursosDeTabla(cursos);

    return (
        <div style={{ width: '100%', position: 'absolute', left: '1px' }}>
            <Table celled definition columns='6'>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            <Loader active={cargando} inline='centered' />
                        </Table.HeaderCell>
                        {DIAS.map(dia => (
                            <Table.HeaderCell key={dia} textAlign='center' content={dia} />
                        ))}
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {bloquesHorarios.map(bloque => (
                        <Table.Row key={bloque.nombre} verticalAlign='top'>
                            <Table.Cell width='1' content={bloque.nombre} verticalAlign='middle' />
                            {bloque.dias.map((dia, indice) => (
                                <Table.Cell key={indice} width='3'>
                                    {dia.cursos.map(curso => (
                                        <Curso
                                            key={curso.idCurso}
                                            {...curso}
                                            mePresionaron={alClickearCurso}
                                        />
                                    ))}
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
};

function transformarRespuestaACursosDeTabla(listaDeCursos: CursosDTO) {
    // Este arreglo funciona casi como un árbol:
    // Cada bloqueHorario tiene un nombre y un arreglo de días que pertenecen
    // a ese bloque horario.
    // Cada día tiene un arreglo con los cursos que pertenecen a ese día (de
    // ese bloque horario). El arreglo de días (de cada bloque horario)
    // tiene siempre longitud 5 (en realidad el número de días en el
    // arreglo DIAS).
    // El arreglo final podría lucir algo así:
    // [
    //   {
    //     nombre: '08:00-10:00',
    //     dias: [
    //       { cursos: [{ /* aquí va cada curso */ }] }, // 1er elemento: lunes
    //       { cursos: [{ /* aquí va cada curso */ }] }, // 2do elemento: martes
    //       { cursos: [{ /* aquí va cada curso */ }] }, // y así...
    //       { cursos: [] },
    //       { cursos: [] },
    //       { cursos: [] }, // viernes
    //     ]
    //   },
    //   {
    //     nombre: '12:30-14:30',
    //     dias: [
    //       { cursos: [] }, // lunes
    //       { cursos: [] }, // martes
    //       { cursos: [] },
    //       { cursos: [] },
    //       { cursos: [] },
    //       { cursos: [] }, // viernes
    //     ]
    //   },
    // ]
    //
    // Todo esto solo funciona correctamente si el arreglo a transformar viene ordenado por
    // bloque horario y luego por día.
    const bloquesHorarios: BloqueHorario[] = [];

    let ultimoNombreDeBloqueHorarioVisto = '';

    for (const curso of listaDeCursos) {
        if (nombreDeBloqueHorario(curso.bloqueHorario) !== ultimoNombreDeBloqueHorarioVisto) {
            // Si hay un nuevo bloqueHorario, bien porque es el pimero o
            // porque el último que se había visto cambió, se debe crear
            // un nuevo bloque horario en el arreglo. Esto implica crear
            // 5 días (en realidad len(DIAS) - 1), cada uno con un arreglo
            // vacío de cursos en su interior
            bloquesHorarios.push({
                nombre: nombreDeBloqueHorario(curso.bloqueHorario),
                dias: DIAS.map(() => ({ cursos: [] })),
            });
            ultimoNombreDeBloqueHorarioVisto = nombreDeBloqueHorario(curso.bloqueHorario);
        }

        // Este es el último bloque horario insertado, ya sea porque se
        // acaba de crear uno nuevo o porque aún no se ha cambiado de bloque
        // horario.
        const ultimoBloqueHorario = bloquesHorarios[bloquesHorarios.length - 1];

        // ultimoBloqueHorario.dias es un arreglo de longitud 5 (len(DIAS) - 1).
        // Como curso.dia es un número entre 1 y 5 (de lunes a martes), podemos
        // referenciar ese día de ese bloque horario como curso.dia - 1, dado
        // que el bloqueHorario ya fue inicializado antes con la cantidad
        // necesaria de días por bloque horario.

        ultimoBloqueHorario.dias[curso.dia - 1].cursos.push({
            idCurso: curso.id,
            color: curso.colorDeSugerencia,
            nombre: curso.nombre,
            sala: curso.sala.toString(),
            inscritos: `${curso.inscritos}/${curso.capacidad}`,
            estado:
                curso.estado.tipo === 'INSCRIBIBLE'
                    ? EstadoCurso.Inscribible
                    : curso.estado.tipo === 'NO_INSCRIBIBLE'
                    ? EstadoCurso.NoInscribible
                    : EstadoCurso.Inscrito,
            sinCupo:
                (curso.estado.tipo === 'NO_INSCRIBIBLE' || curso.estado.tipo === 'INSCRITO') &&
                !curso.estado.hayCupo,
            mensajeSiDesactivado:
                curso.estado.tipo === 'NO_INSCRIBIBLE' ? curso.estado.mensaje : undefined,
        });
    }

    return bloquesHorarios;
}

export default TablaDeCursos;

function nombreDeBloqueHorario(bloqueHorario: BloqueHorarioDTO) {
    const tipo = bloqueHorario.tipo === 'vespertino' ? '-v' : '';
    return `${bloqueHorario.horaInicio}-${bloqueHorario.horaTermino}${tipo}`;
}



// WEBPACK FOOTER //
// ./src/modulos/Horarios/TomaDeHorarios/componentes/Inscribir/Cursos/componentes/TablaDeCursos/index.tsx